<script lang="ts" setup>
import { computed } from "vue";
import { useProducts } from "@/queries/products";
import { useProjectRuns, useProjectRunStats } from "@/queries/projects";
import { useActivities } from "@/queries/activities";
import ActivityFeed from "@/views/shared/components/ActivityFeed.vue";
import { getProjectStatus } from "../../utils/projectStatus";
import type { Project } from "@/types";

interface Props {
  project: Project | null;
  productName: string;
  // Product version number the project is subscribed to (e.g. "2.1"); undefined for publisher-master
  // projects and unsynced subscribers, in which case the version pill is omitted.
  productVersion?: string;
  isProductlessProject?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isProductlessProject: false,
});

const emit = defineEmits<{
  openConfigWizard: [];
  setActiveTab: [tab: string];
}>();

const { products } = useProducts();

const isPublisherMasterProject = computed(() => {
  if (!props.project?.publisherProductId) return false;
  const product = products.value.find(
    (p) => p.id === props.project?.publisherProductId
  );
  if (!product) return false;

  return product.masterProjectId === props.project.id;
});

// Status label via the shared util so this banner agrees with the detail header and dashboard
// badge. isReady isn't available at this level, so the util falls back to lifecycle-only.
const statusLabel = computed(() => getProjectStatus(props.project));

const showStatusCard = computed(
  () => statusLabel.value === "Setup Required" || statusLabel.value === "Error"
);

const projectId = computed(() => props.project?.id ?? "");
const { projectRuns, projectRunsLoading } = useProjectRuns(projectId);
// Stat cards use a dedicated server-side aggregate so they don't depend on how many
// runs the (paged) projectRuns list happened to fetch.
const { projectRunStats } = useProjectRunStats(projectId);

// Project lifecycle activity — workflow publishes, member events, project edits. Distinct
// from the runs list (operational data) which lives on the History tab.
const { activities: projectActivities, activitiesLoading: projectActivitiesLoading } =
  useActivities({ projectId, take: 10 });

const sortedRuns = computed(() =>
  [...projectRuns.value].sort(
    (a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
  )
);

const recentRuns = computed(() => sortedRuns.value.slice(0, 5));

const projectMetrics = computed<{
  totalRuns: number;
  successRate: number | string;
  avgDuration: string;
  avgDurationUnit: string;
}>(() => {
  const stats = projectRunStats.value;
  if (!stats || stats.totalRuns === 0) {
    return { totalRuns: 0, successRate: "--", avgDuration: "--", avgDurationUnit: "" };
  }

  const successRate = Math.round((stats.succeededCount / stats.totalRuns) * 1000) / 10;
  const avgMs = stats.avgDurationMs;

  let avgDuration: string;
  let avgDurationUnit: string;
  if (avgMs <= 0) {
    avgDuration = "--";
    avgDurationUnit = "";
  } else if (avgMs < 1000) {
    avgDuration = `${Math.round(avgMs)}`;
    avgDurationUnit = "ms";
  } else if (avgMs < 60000) {
    avgDuration = `${(avgMs / 1000).toFixed(1)}`;
    avgDurationUnit = "sec";
  } else {
    avgDuration = `${(avgMs / 60000).toFixed(1)}`;
    avgDurationUnit = "min";
  }

  return { totalRuns: stats.totalRuns, successRate, avgDuration, avgDurationUnit };
});

const projectConfig = computed(() => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return {
    created: formatDate(props.project?.createdAt),
    lastModified: formatDate(props.project?.updatedAt),
  };
});

function formatRelativeTime(dateString: string | Date): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes === 1 ? "" : "s"} ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function getStatusClass(status: string): string {
  switch (status) {
    case "Succeeded":
      return "bg-success";
    case "Failed":
      return "bg-danger";
    case "Running":
      return "bg-primary";
    case "Warning":
      return "bg-warning";
    default:
      return "bg-secondary";
  }
}

function getStatusText(status: string): string {
  switch (status) {
    case "Succeeded":
      return "Success";
    case "Failed":
      return "Failed";
    case "Running":
      return "Running";
    case "Warning":
      return "Warning";
    default:
      return status;
  }
}

</script>

<template>
  <div class="tab-pane fade show active">
    <!-- Compact Status Banner -->
    <div v-if="showStatusCard" class="mb-3">
      <div
        v-if="statusLabel === 'Setup Required'"
        class="compact-banner setup-banner"
      >
        <div class="d-flex justify-content-between align-items-center">
          <div class="banner-info">
            <i class="bi bi-exclamation-triangle me-2"></i>
            Configuration Required •
            Some settings are missing to activate this project.
          </div>
          <button
            class="bb-btn-primary-cta btn-sm overview-config-btn"
            @click="emit('openConfigWizard')"
          >
            <i class="bi bi-gear me-1"></i> Configure
          </button>
        </div>
      </div>

      <div
        v-else-if="statusLabel === 'Error'"
        class="compact-banner error-banner"
      >
        <div class="d-flex justify-content-between align-items-center">
          <div class="banner-info">
            <i class="bi bi-x-circle me-2"></i>
            Configuration Error • Connection or configuration errors need to be
            resolved.
          </div>
          <button
            class="bb-btn-secondary-cta btn-sm"
            @click="emit('setActiveTab', 'connections')"
          >
            <i class="bi bi-plug me-1"></i> Check Connections
          </button>
        </div>
      </div>
    </div>

    <!-- Metrics Row -->
    <div class="row mb-4 metrics-row">
      <div class="col-4">
        <div class="card text-center">
          <div class="card-body">
            <p class="h2 text-primary mb-2">{{ projectMetrics.totalRuns }}</p>
            <p class="card-subtitle mb-2 text-muted">Total Runs</p>
          </div>
        </div>
      </div>
      <div class="col-4">
        <div class="card text-center">
          <div class="card-body">
            <p class="h2 text-success mb-2">{{ projectMetrics.successRate }}%</p>
            <p class="card-subtitle mb-2 text-muted">Success Rate</p>
          </div>
        </div>
      </div>
      <div class="col-4">
        <div class="card text-center">
          <div class="card-body">
            <p class="h2 text-info mb-2">{{ projectMetrics.avgDuration }}<small v-if="projectMetrics.avgDurationUnit" class="fs-6 text-muted ms-1">{{ projectMetrics.avgDurationUnit }}</small></p>
            <p class="card-subtitle mb-2 text-muted">Avg Duration</p>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-3 g-lg-4">
      <div class="col-lg-7">
        <!-- Recent Workflow Runs -->
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h2 class="card-title mb-0">Recent Workflow Runs</h2>
              <button
                v-if="recentRuns.length > 0"
                type="button"
                class="bb-btn-secondary-cta btn-sm"
                @click="emit('setActiveTab', 'history')"
              >
                View all<i class="bi bi-arrow-right ms-1"></i>
              </button>
            </div>
            <div v-if="projectRunsLoading" class="text-center py-4">
              <div class="spinner-border spinner-border-sm text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
            <div v-else-if="recentRuns.length === 0" class="text-center py-4">
              <i class="bi bi-clock-history text-muted" style="font-size: 2rem;"></i>
              <p class="text-muted mt-2 mb-0">No workflow runs yet</p>
            </div>
            <div v-else class="workflow-runs">
              <div v-for="run in recentRuns" :key="run.id" class="run-item">
                <div class="run-info">
                  <div class="run-name">{{ run.workflowName }}</div>
                  <div class="run-timestamp">{{ formatRelativeTime(run.startedAt) }}</div>
                </div>
                <div class="run-status">
                  <span class="badge" :class="getStatusClass(run.status)">
                    {{ getStatusText(run.status) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-5">
        <!-- Project Summary -->
        <div class="card">
          <div class="card-body">
            <h2 class="card-title">Project Summary</h2>
            <div class="config-info">
              <div v-if="!props.isProductlessProject" class="config-item">
                <strong>Product:</strong>
                <router-link
                  v-if="props.project?.publisherProductId"
                  :to="`/publisher/${props.project.publisherProductId}`"
                  class="ms-1 text-decoration-none d-inline-flex align-items-center gap-1"
                >
                  <i
                    v-if="isPublisherMasterProject"
                    class="bi bi-bookmark-star-fill text-info"
                    title="Publisher Master Project"
                    style="font-size: 0.9rem;"
                  ></i>
                  <span class="text-primary">{{ productName }}</span>
                  <span v-if="props.productVersion" class="badge bg-secondary fw-normal">v{{ props.productVersion }}</span>
                  <i class="bi bi-box-arrow-up-right ms-1 small"></i>
                </router-link>
                <span v-else class="ms-1">
                  {{ productName }}
                  <span v-if="props.productVersion" class="badge bg-secondary fw-normal ms-1">v{{ props.productVersion }}</span>
                </span>
              </div>
              <div class="config-item">
                <strong>Created:</strong> {{ projectConfig.created }}
              </div>
              <div class="config-item">
                <strong>Last Modified:</strong> {{ projectConfig.lastModified }}
              </div>
            </div>
          </div>
        </div>

        <!-- Recent Activity — sits under Project Summary in the right column so the
             stack balances the height of Recent Workflow Runs once that list grows. -->
        <div class="card mt-3">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h2 class="card-title mb-0">Recent Activity</h2>
              <small v-if="projectActivitiesLoading" class="text-muted">Loading…</small>
            </div>
            <ActivityFeed
              :activities="projectActivities"
              :loading="projectActivitiesLoading"
              empty-text="No project activity yet. Workflow publishes, member changes, and project edits will appear here."
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
/* Component-specific styles only - common styles moved to global CSS */

/* Overview configure button styling */
.overview-config-btn {
  white-space: nowrap;
  min-width: 90px;
}

.run-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--bs-border-color-translucent);
}

.run-item:last-child {
  border-bottom: none;
}

.run-info .run-name {
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: var(--bs-body-color);
}

.run-info .run-timestamp {
  font-size: 0.875rem;
  color: var(--bs-secondary-color);
}


.publisher-master-badge {
  //background: linear-gradient(135deg, #33658a 0%, #5b9bd1 100%) !important;
  //color: white;
  font-size: 0.75rem;
  font-weight: 500;
  border: none;
}

.compact-banner {
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 14px;
}

.setup-banner {
  background-color: var(--bs-warning-bg-subtle);
  border: 1px solid var(--bs-warning-border-subtle);
}

.setup-banner .banner-info {
  color: var(--bs-warning-text-emphasis);
  font-weight: 500;
}

.error-banner {
  background-color: var(--bs-danger-bg-subtle);
  border: 1px solid var(--bs-danger-border-subtle);
}

.error-banner .banner-info {
  color: var(--bs-danger-text-emphasis);
  font-weight: 500;
}

.compact-banner .btn-sm {
  font-size: 12px;
  padding: 4px 8px;
}

.publisher-product-btn {
  border-color: var(--bs-primary);
  color: var(--bs-primary);
}

[data-bs-theme="dark"] .publisher-product-btn {
  border-color: var(--bs-gray-600);
  color: var(--bs-gray-300);
  background-color: var(--bs-gray-700);
}

[data-bs-theme="dark"] .publisher-product-btn:hover {
  background-color: var(--bs-gray-600);
  border-color: var(--bs-gray-500);
  color: var(--bs-gray-100);
}

</style>
